import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { persistStorage } from '@/stores/persistStorage';

export type ScheduleStyle = 'structured' | 'flexible' | 'ai-driven';
export type RoutineTemplate = 'morning' | 'winddown' | 'workstart';

export interface EnergyPattern {
  peakStart: number;
  peakEnd: number;
}

export interface OnboardingState {
  hasCompletedOnboarding: boolean;
  hasSeenAuthPrompt: boolean;
  scheduleStyle: ScheduleStyle | null;
  energyPattern: EnergyPattern | null;
  selectedRoutineTemplate: RoutineTemplate | null;
  completeOnboarding: () => void;
  setHasSeenAuthPrompt: (seen: boolean) => void;
  setScheduleStyle: (style: ScheduleStyle | null) => void;
  setEnergyPattern: (pattern: EnergyPattern) => void;
  setSelectedRoutineTemplate: (template: RoutineTemplate | null) => void;
  resetOnboarding: () => void;
}

const initialState = {
  hasCompletedOnboarding: false,
  hasSeenAuthPrompt: false,
  scheduleStyle: null as ScheduleStyle | null,
  energyPattern: null as EnergyPattern | null,
  selectedRoutineTemplate: null as RoutineTemplate | null,
};

export const useOnboardingStore = create<OnboardingState>()(
  persist(
    (set) => ({
      ...initialState,
      completeOnboarding: () => {
        set({ hasCompletedOnboarding: true });
        void import('@/stores/uiStore').then(({ useUIStore }) => {
          const state = useOnboardingStore.getState();
          if (state.scheduleStyle) useUIStore.getState().setScheduleStyle(state.scheduleStyle);
          if (state.energyPattern) {
            const { peakStart, peakEnd } = state.energyPattern;
            useUIStore.getState().setEnergyPattern({
              peakStart,
              peakEnd,
              creativeStart: peakEnd + 2,
              creativeEnd: peakEnd + 5,
              winddownStart: 19,
              winddownEnd: 22,
            });
          }
        });
      },
      setHasSeenAuthPrompt: (hasSeenAuthPrompt) => set({ hasSeenAuthPrompt }),
      setScheduleStyle: (scheduleStyle) => set({ scheduleStyle }),
      setEnergyPattern: (energyPattern) => set({ energyPattern }),
      setSelectedRoutineTemplate: (selectedRoutineTemplate) => set({ selectedRoutineTemplate }),
      resetOnboarding: () => set(initialState),
    }),
    {
      name: 'brainday:onboarding',
      storage: persistStorage<OnboardingState>(),
      partialize: (state) =>
        ({
          hasCompletedOnboarding: state.hasCompletedOnboarding,
          hasSeenAuthPrompt: state.hasSeenAuthPrompt,
          scheduleStyle: state.scheduleStyle,
          energyPattern: state.energyPattern,
          selectedRoutineTemplate: state.selectedRoutineTemplate,
        }) as OnboardingState,
    },
  ),
);
